import { Box } from "@repo/components/src/ui/box";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Spinner } from "@repo/components/src/ui/spinner";
import { useEvent } from "expo";
import { useVideoPlayer, VideoView } from "expo-video";
import { Play } from "lucide-react-native";
import React, { useRef, useState } from "react";

type Props = {
  contentUrl: string | null | undefined;
  thumbnailUrl: string | null | undefined;
  alt: string;
  /** Fill the parent's height instead of deriving it from a 9:16 aspect ratio.
   *  The post-detail modal needs this: at the column's width a 9:16 box is
   *  taller than the modal body, so the actions beneath it were pushed out of
   *  view. Letterboxes with `contain` so the whole frame still shows. */
  fill?: boolean;
};

const DeliverableVideo = React.memo(
  ({ contentUrl, thumbnailUrl, alt, fill = false }: Props) => {
    const player = useVideoPlayer(contentUrl ?? null, (p) => {
      p.loop = false;
    });
    const videoRef = useRef<VideoView>(null);
    const [started, setStarted] = useState(false);
    const { status } = useEvent(player, "statusChange", {
      status: player.status,
    });

    const handlePlay = () => {
      setStarted(true);
      player.play();
    };

    return (
      <Box
        className={`bg-typography-900 rounded-xl overflow-hidden relative w-full ${
          fill ? "h-full" : "aspect-[9/16]"
        }`}
      >
        {contentUrl ? (
          <VideoView
            ref={videoRef}
            player={player}
            nativeControls
            contentFit={fill ? "contain" : "cover"}
            className="w-full h-full"
          />
        ) : null}

        {!started ? (
          <Pressable
            onPress={handlePlay}
            disabled={!contentUrl}
            className="absolute inset-0"
          >
            {thumbnailUrl ? (
              <Image
                source={{ uri: thumbnailUrl }}
                alt={alt}
                size="none"
                className="flex-1"
                contentFit={fill ? "contain" : "cover"}
                contentPosition="center"
              />
            ) : null}
            {contentUrl ? (
              <Box className="absolute inset-0 items-center justify-center">
                <Box className="bg-background-0/80 rounded-full p-4">
                  <Icon as={Play} size="xl" className="text-typography-900" />
                </Box>
              </Box>
            ) : null}
          </Pressable>
        ) : null}

        {started && status === "loading" ? (
          <Box className="absolute inset-0 items-center justify-center bg-background-900/55">
            <Spinner size="large" />
          </Box>
        ) : null}
      </Box>
    );
  },
);

DeliverableVideo.displayName = "DeliverableVideo";

export default DeliverableVideo;
